import React, { useRef, useState, useEffect } from "react";
import api from "@/lib/api";
import { Mic, Square, Sparkles, X, Copy, Check, FileText, CalendarPlus, Loader2 } from "lucide-react";
import RichText from "@/components/RichText";
import { toast } from "sonner";

/**
 * VoiceStatementModal. Records a voice statement, transcribes it, polishes
 * it with the AI, and lets the user decide what to do with the result.
 *
 * Post-transcription UX (the part the user was getting stuck on):
 *   1. Polished statement is shown in a read-only RichText view by default
 *      (with Annex links rendered nicely).
 *   2. "Edit text" toggles a plain textarea so the user can amend the draft
 *      before saving anywhere.
 *   3. "What would you like to do with this?" presents three explicit
 *      destinations: Save to chronology (timeline event), Save as document
 *      (.txt file in the case vault), Copy text (clipboard). Each one shows
 *      a success state so the user knows it landed.
 */
export default function VoiceStatementModal({ caseId, caseState = "QLD", onClose }) {
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [annexMap, setAnnexMap] = useState({});
  // Editable working copy of the polished statement. Seeded from result.statement
  // on first arrival, then lives independently so the user can amend before
  // saving / copying / uploading.
  const [statementDraft, setStatementDraft] = useState("");
  const [editing, setEditing] = useState(false);
  // Action button busy flags + saved-state flags to prevent double-creates.
  const [savingTimeline, setSavingTimeline] = useState(false);
  const [savingDoc, setSavingDoc] = useState(false);
  const [savedTimelineId, setSavedTimelineId] = useState(null);
  const [savedDocId, setSavedDocId] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef();

  useEffect(() => {
    api.get(`/cases/${caseId}/annexes`).then(({ data }) => {
      const m = {};
      data.forEach((d) => { m[d.annex_number] = d; });
      setAnnexMap(m);
    });
  }, [caseId]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) {
      alert("Microphone access denied.");
    }
  };
  const stop = () => { mediaRef.current?.stop(); setRecording(false); };

  const submit = async () => {
    if (!audioBlob) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", new File([audioBlob], `statement-${Date.now()}.webm`, { type: "audio/webm" }));
      const { data } = await api.post(`/cases/${caseId}/voice-statement`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setStatementDraft(data?.statement || "");
      setSavedTimelineId(null);
      setSavedDocId(null);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to draft statement");
    } finally {
      setBusy(false);
    }
  };

  const handleFile = (file) => { if (file) setAudioBlob(file); };

  const copy = () => {
    navigator.clipboard.writeText(statementDraft || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const saveToChronology = async () => {
    if (!statementDraft.trim()) {
      toast.error("Add some text first.");
      return;
    }
    setSavingTimeline(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const summary = statementDraft.split(/\s+/).slice(0, 12).join(" ");
      const { data } = await api.post(`/cases/${caseId}/timeline`, {
        title: `Voice statement, ${today}`,
        description: statementDraft,
        event_date: today,
      });
      setSavedTimelineId(data?.id || true);
      toast.success(`Added to chronology. "${summary}…"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save to chronology.");
    } finally {
      setSavingTimeline(false);
    }
  };

  const saveAsDocument = async () => {
    if (!statementDraft.trim()) {
      toast.error("Add some text first.");
      return;
    }
    setSavingDoc(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const filename = `voice-statement-${today}.txt`;
      const blob = new Blob([statementDraft], { type: "text/plain" });
      const fd = new FormData();
      fd.append("file", new File([blob], filename, { type: "text/plain" }));
      fd.append("doc_type", "note");
      fd.append("event_date", today);
      const { data } = await api.post(`/cases/${caseId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSavedDocId(data?.id || true);
      toast.success("Saved as document in the vault.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save as document.");
    } finally {
      setSavingDoc(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#FAF9F6] border border-[#E5E4DE] rounded-sm max-w-2xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="voice-statement-modal"
      >
        <div className="flex justify-between items-center p-6 border-b border-[#E5E4DE]">
          <div className="flex items-center gap-2">
            <Mic size={18} strokeWidth={1.5} className="text-[#1B6C62]" />
            <h3 className="font-serif text-2xl">Voice &rarr; Statement</h3>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5"><X size={18} strokeWidth={1.5} /></button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-[#6B7280] leading-relaxed">
            Tell the story of what happened. The AI will transcribe and shape it into a clean tribunal-ready statement, referencing your uploaded documents by their <span className="font-mono">Annex N</span> numbers where appropriate.
          </p>

          {!audioBlob && (
            <div className="text-center py-6">
              <button onClick={recording ? stop : start} className={`btn-primary inline-flex items-center gap-2 ${recording ? "bg-[#B35D4C] hover:bg-[#9c4d3e]" : ""}`} data-testid="record-statement">
                {recording ? (<><Square size={14} strokeWidth={2} fill="currentColor" /> Stop recording</>) : (<><Mic size={14} strokeWidth={1.5} /> Start recording</>)}
              </button>
              <p className="text-xs text-[#6B7280] mt-3">or <button onClick={() => fileInputRef.current?.click()} className="text-[#0D2B52] underline">upload an audio file</button></p>
              <input ref={fileInputRef} type="file" accept="audio/*" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
            </div>
          )}

          {audioBlob && !result && (
            <div className="text-center py-4 space-y-3">
              <audio controls src={URL.createObjectURL(audioBlob)} className="mx-auto" />
              <div className="flex gap-2 justify-center">
                <button onClick={() => setAudioBlob(null)} className="btn-secondary text-sm">Re-record</button>
                <button onClick={submit} disabled={busy} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="submit-voice">
                  <Sparkles size={14} strokeWidth={1.5} /> {busy ? "Transcribing & drafting…" : "Draft my statement"}
                </button>
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-4" data-testid="statement-result">
              <div>
                <div className="label-mono mb-2">Transcript (raw)</div>
                <div className="bg-white border border-[#E5E4DE] p-4 text-sm text-[#6B7280] italic max-h-32 overflow-y-auto" data-testid="statement-transcript">{result.transcript}</div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <div className="label-mono">Polished statement</div>
                  <button
                    onClick={() => setEditing((v) => !v)}
                    className="btn-ghost text-xs inline-flex items-center gap-1"
                    data-testid="statement-toggle-edit"
                  >
                    {editing ? "Done editing" : "Edit text"}
                  </button>
                </div>

                {editing ? (
                  <textarea
                    value={statementDraft}
                    onChange={(e) => setStatementDraft(e.target.value)}
                    className="w-full min-h-[260px] bg-white border border-[#1B6C62] p-5 text-[15px] leading-relaxed font-serif text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#1B6C62]/40"
                    data-testid="statement-textarea"
                  />
                ) : (
                  <div className="bg-white border border-[#E5E4DE] p-5 text-[15px] leading-relaxed font-serif whitespace-pre-wrap text-[#1A1A1A]">
                    {statementDraft === (result.statement || "") ? (
                      <RichText text={statementDraft} annexMap={annexMap} state={caseState} />
                    ) : (
                      statementDraft
                    )}
                  </div>
                )}

                <p className="text-xs text-[#6B7280] italic mt-3">
                  References &ldquo;Annex N&rdquo; point to your documents in chronological order. Review before filing.
                </p>
              </div>

              {/* What would you like to do with this statement? */}
              <div className="bg-[#FAF6E8] border-l-4 border-l-[#C9A961] p-4" data-testid="statement-actions">
                <div className="label-mono mb-3 text-[#8A6D3B]">What would you like to do with this?</div>
                <div className="grid sm:grid-cols-3 gap-2">
                  <button
                    onClick={saveToChronology}
                    disabled={savingTimeline || !statementDraft.trim()}
                    className="btn-teal text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                    data-testid="statement-save-chronology"
                  >
                    {savingTimeline ? (
                      <Loader2 size={13} strokeWidth={1.75} className="animate-spin" />
                    ) : savedTimelineId ? (
                      <Check size={13} strokeWidth={2} />
                    ) : (
                      <CalendarPlus size={13} strokeWidth={1.75} />
                    )}
                    {savedTimelineId ? "Added to chronology" : "Save to chronology"}
                  </button>

                  <button
                    onClick={saveAsDocument}
                    disabled={savingDoc || !statementDraft.trim()}
                    className="btn-secondary text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                    data-testid="statement-save-document"
                  >
                    {savingDoc ? (
                      <Loader2 size={13} strokeWidth={1.75} className="animate-spin" />
                    ) : savedDocId ? (
                      <Check size={13} strokeWidth={2} />
                    ) : (
                      <FileText size={13} strokeWidth={1.75} />
                    )}
                    {savedDocId ? "Saved to vault" : "Save as document"}
                  </button>

                  <button
                    onClick={copy}
                    disabled={!statementDraft.trim()}
                    className="btn-secondary text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                    data-testid="statement-copy"
                  >
                    {copied ? (<><Check size={13} strokeWidth={2} /> Copied</>) : (<><Copy size={13} strokeWidth={1.5} /> Copy text</>)}
                  </button>
                </div>
                <p className="text-[11px] text-[#8A6D3B]/80 mt-3 leading-snug">
                  Tip: Save to chronology adds a dated event to your timeline. Save as document drops a .txt copy into the Documents tab so you can attach or share it later.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
